import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import AvailableServices, {
  BasketMode,
} from "@/components/PostAddToBasketDrawer/components/AvailableServices.tsx";
import { getScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import { ProtectPlusComplianceDrawerUI } from "@/components/PostAddToBasketDrawer/ProtectPlusComplianceDrawerUI.tsx";
import { useSharedServices } from "@/utils/services/useSharedServices";

type Props = Partial<StandardProps> &
  Pick<StandardProps, "product" | "runtimeConfig" | "flags">;

export function PostAddToBasketDrawer({
  configProps,
  product,
  runtimeConfig,
  flags,
}: Props) {
  const { variant, isSelected } = useVariant(product);
  const { availableServices, actions } = useSharedServices({ product, flags });

  const { services = [] } = variant;
  const { brand } = product;

  const isShipFromStore = variant.availability?.shipFromStore === true;
  const expressCheckoutEnabled = configProps?.expressCheckoutEnabled === true;
  const mandatoryServicesOverride = flags.mandatoryServicesOverride.value
    .toString()
    .split(",");

  const appleProduct = brand?.name.toLowerCase() === "apple";

  const deliveryAndWarrantyServices = services.filter(({ __typename }) =>
    ["DeliveryService", "ExtendedWarranty"].includes(__typename),
  );

  const guarantees = services.filter(
    ({ __typename }) => __typename === "IncludedGuarantee",
  );

  const warrantyServices = services.filter(
    ({ __typename }) => __typename === "ExtendedWarranty",
  );

  const activeProduct = ensureIsActive(product);

  const activeVariant = ensureIsActiveVariant(variant);

  if (!activeProduct || !activeVariant) return null;

  const isOutOfStock = !variant.availability?.availableToOrder;

  let mode: BasketMode = BasketMode.Post;

  if (isShipFromStore || expressCheckoutEnabled || appleProduct) {
    mode = BasketMode.Pre;
  }

  const protectPlusScheme = getScheme(product);

  return (
    <>
      <AvailableServices
        variantServices={warrantyServices}
        protectPlusScheme={protectPlusScheme}
        mode={mode}
      />
      <ProtectPlusComplianceDrawerUI
        basketMode={mode}
        title={variant.title}
        variantServices={deliveryAndWarrantyServices}
        basketBaseUrl={runtimeConfig.basketApiBaseUrl}
        productId={activeProduct?.aliases.pimProductId}
        skuId={activeVariant?.aliases.skuId}
        guarantees={guarantees}
        protectPlusScheme={protectPlusScheme}
        hideFooter={isOutOfStock || !isSelected}
        mandatoryServicesOverride={mandatoryServicesOverride}
        availableServices={availableServices}
        actions={actions}
      />
    </>
  );
}
